import React, { useEffect, useState, useRef } from "react";
import { useNavigate, useSearchParams } from "react-router-dom";
import DashboardLayout from "../../../components/DashboardLayout";
import Loader from "../../../components/Loader";
import { showErrorToastMsg } from "../../../utility/ToastMessage";
import {
  getSeminarByIdAPI,
  getSeminarFilesAPI,
} from "../../../services/portal/student/studentSeminarService";
import StudyPackCard from "../../../components/portal/StudyPackCard";
import { RoutePaths } from "../../../utility/RoutePaths";
import BunnyVideo from "../../../components/BunneyVideo";
import Plyr from "plyr";
import "plyr/dist/plyr.css";
import PaymentBanner from "../../../components/portal/PaymentBanner";
import PaymentPendingVerification from "../../../components/portal/PaymentPendingVerification";

const extractYouTubeVideoId = (url) => {
  if (!url) return null;
  const patterns = [
    /(?:youtube\.com\/watch\?v=|youtu\.be\/|youtube\.com\/embed\/)([^&\n?#]+)/,
    /youtube\.com\/.*[?&]v=([^&\n?#]+)/,
    /youtube\.com\/live\/([^&\n?#]+)/,
  ];
  for (const pattern of patterns) {
    const match = url.match(pattern);
    if (match && match[1]) return match[1];
  }
  return null;
};

const SeminarDetailsPage = () => {
  const navigate = useNavigate();
  const [searchParams] = useSearchParams();
  const seminarId = searchParams.get("seminarId");

  const [paymentStatus, setPaymentStatus] = useState("");
  const [loading, setLoading] = useState(true);
  const [seminar, setSeminar] = useState(null);
  const [files, setFiles] = useState([]);

  const playerRef = useRef(null);
  const plyrInstance = useRef(null);

  const youtubeVideoId = seminar
    ? extractYouTubeVideoId(seminar.VideoLink)
    : null;

  useEffect(() => {
    if (!youtubeVideoId || !playerRef.current) return;

    plyrInstance.current = new Plyr(playerRef.current, {
      title: "",
      controls: [
        "play-large",
        "play",
        "progress",
        "current-time",
        "mute",
        "volume",
        "fullscreen",
      ],
      youtube: { noCookie: true, rel: 0, showinfo: 0 },
    });

    return () => {
      plyrInstance.current?.destroy();
      plyrInstance.current = null;
    };
  }, [youtubeVideoId]);

  useEffect(() => {
    if (seminarId) {
      loadData();
    }
  }, [seminarId]);

  const loadData = async () => {
    setLoading(true);
    try {
      const [seminarsRes] = await Promise.all([getSeminarByIdAPI(seminarId)]);

      if (seminarsRes && seminarsRes.IsSuccessful && seminarsRes.Data) {
        setSeminar(seminarsRes.Data);

        if (seminarsRes.Data.SeminarFiles) {
          setFiles(seminarsRes.Data.SeminarFiles);
        }

        setPaymentStatus(seminarsRes.Data.PaymentStatus || "");
      }
    } catch (error) {
      console.error("Error loading seminar details:", error);
      showErrorToastMsg("Failed to load seminar");
    } finally {
      setLoading(false);
    }
  };

  if (!seminarId) {
    return (
      <DashboardLayout title="Seminar Details" subtitle="">
        <div className="text-center py-12">
          <p className="text-gray-600 text-lg">Invalid seminar link.</p>
        </div>
      </DashboardLayout>
    );
  }

  return (
    <DashboardLayout
      title="Seminar Details"
      subtitle={seminar ? seminar.Title : "Loading..."}
    >
      <div className="mb-4">
        <button
          type="button"
          onClick={() => navigate(RoutePaths.STUDENT_SEMINARS_PAGE)}
          className="text-sm font-medium text-primary-600 hover:text-primary-800"
        >
          ← Back to seminars
        </button>
      </div>

      {loading ? (
        <div className="flex justify-center items-center py-12">
          <Loader />
        </div>
      ) : !seminar ? (
        <div className="text-center py-12">
          <p className="text-gray-600 text-lg">Seminar not found.</p>
        </div>
      ) : (
        <div className="space-y-6">
          {/* Seminar Info */}
          <div className="bg-white rounded-lg shadow-sm border border-gray-200 overflow-hidden">
            {seminar.Image && (
              <img
                src={seminar.Image}
                alt={seminar.Title}
                className="w-full h-56 object-cover"
              />
            )}
            <div className="p-6">
              <h2 className="text-2xl font-bold text-gray-900">
                {seminar.Title}
              </h2>
              {seminar.Description && (
                <p className="text-gray-600 mt-2">{seminar.Description}</p>
              )}
              <div className="flex flex-wrap gap-6 mt-4">
                {seminar.Date && (
                  <div className="flex items-center gap-2 text-sm text-gray-600">
                    <svg
                      className="w-4 h-4 text-primary-500"
                      fill="none"
                      stroke="currentColor"
                      viewBox="0 0 24 24"
                    >
                      <path
                        strokeLinecap="round"
                        strokeLinejoin="round"
                        strokeWidth={2}
                        d="M8 7V3m8 4V3m-9 8h10M5 21h14a2 2 0 002-2V7a2 2 0 00-2-2H5a2 2 0 00-2 2v12a2 2 0 002 2z"
                      />
                    </svg>
                    <span className="font-medium">Date:</span>
                    {new Date(seminar.Date).toLocaleDateString("en-US", {
                      year: "numeric",
                      month: "long",
                      day: "numeric",
                    })}
                  </div>
                )}
                {seminar.Fee !== undefined && seminar.Fee !== null && (
                  <div className="flex items-center gap-2 text-sm text-gray-600">
                    <svg
                      className="w-4 h-4 text-primary-500"
                      fill="none"
                      stroke="currentColor"
                      viewBox="0 0 24 24"
                    >
                      <path
                        strokeLinecap="round"
                        strokeLinejoin="round"
                        strokeWidth={2}
                        d="M12 8c-1.657 0-3 .895-3 2s1.343 2 3 2 3 .895 3 2-1.343 2-3 2m0-8c1.11 0 2.08.402 2.599 1M12 8V7m0 1v8m0 0v1m0-1c-1.11 0-2.08-.402-2.599-1M21 12a9 9 0 11-18 0 9 9 0 0118 0z"
                      />
                    </svg>
                    <span className="font-medium">Fee:</span>
                    <span className="font-semibold text-primary-600">
                      Rs. {seminar.Fee.toLocaleString()}
                    </span>
                  </div>
                )}
              </div>
              {paymentStatus === "Paid" && (
                <div className="mt-4 p-4 bg-blue-50 border border-blue-200 rounded-lg flex flex-wrap gap-6">
                  {seminar.ZoomLink && (
                    <div className="flex items-center gap-2 text-sm text-gray-700">
                      <svg
                        className="w-4 h-4 text-blue-500"
                        fill="none"
                        stroke="currentColor"
                        viewBox="0 0 24 24"
                      >
                        <path
                          strokeLinecap="round"
                          strokeLinejoin="round"
                          strokeWidth={2}
                          d="M15 10l4.553-2.276A1 1 0 0121 8.618v6.764a1 1 0 01-1.447.894L15 14M3 8a2 2 0 012-2h8a2 2 0 012 2v8a2 2 0 01-2 2H5a2 2 0 01-2-2V8z"
                        />
                      </svg>
                      <span className="font-medium">Zoom Link:</span>
                      <a
                        href={seminar.ZoomLink}
                        target="_blank"
                        rel="noopener noreferrer"
                        className="text-blue-600 hover:text-blue-800 underline font-semibold"
                      >
                        Join Meeting
                      </a>
                    </div>
                  )}
                  {seminar.Password && (
                    <div className="flex items-center gap-2 text-sm text-gray-700">
                      <svg
                        className="w-4 h-4 text-blue-500"
                        fill="none"
                        stroke="currentColor"
                        viewBox="0 0 24 24"
                      >
                        <path
                          strokeLinecap="round"
                          strokeLinejoin="round"
                          strokeWidth={2}
                          d="M12 15v2m-6 4h12a2 2 0 002-2v-6a2 2 0 00-2-2H6a2 2 0 00-2 2v6a2 2 0 002 2zm10-10V7a4 4 0 00-8 0v4h8z"
                        />
                      </svg>
                      <span className="font-medium">Password:</span>
                      <span className="font-semibold">{seminar.Password}</span>
                    </div>
                  )}
                </div>
              )}
            </div>
          </div>

          {seminar &&
            (seminar.StudentSeminarAssignStatus === 0 ? (
              <PaymentBanner
                price={seminar.Fee}
                classId={seminar.SeminarId}
                type={2}
              />
            ) : seminar.StudentSeminarAssignStatus === 1 ? (
              <PaymentPendingVerification
                payment={[seminar]}
                classId={seminar.SeminarId}
              />
            ) : null)}

          {/* Video */}
          {paymentStatus === "Paid" && seminar.VideoLink && (
            <>
              <div className="bg-white rounded-lg shadow-sm border border-gray-200 p-6">
                <h3 className="text-lg font-bold text-gray-900 mb-4">Video</h3>
                {youtubeVideoId ? (
                  <>
                    <style>{`
                    .plyr__title { display: none !important; }
                    .plyr__video-embed::before {
                      content: '';
                      position: absolute;
                      top: 0;
                      left: 0;
                      width: 100%;
                      height: 15%;
                      z-index: 10;
                      pointer-events: all;
                      background: transparent;
                    }
                  `}</style>
                    <div
                      ref={playerRef}
                      data-plyr-provider="youtube"
                      data-plyr-embed-id={youtubeVideoId}
                    />
                  </>
                ) : (
                  <BunnyVideo videoUrl={seminar.VideoLink} />
                )}
              </div>

              <div className="bg-white rounded-lg shadow-sm border border-gray-200 p-6">
                <h3 className="text-lg font-bold text-gray-900 mb-4">Files</h3>
                {files.length === 0 ? (
                  <p className="text-gray-500 text-sm">
                    No files available for this seminar.
                  </p>
                ) : (
                  <div className="flex flex-wrap gap-4">
                    {files.map((file) => (
                      <StudyPackCard key={file.Id} document={file} />
                    ))}
                  </div>
                )}
              </div>
            </>
          )}
        </div>
      )}
    </DashboardLayout>
  );
};

export default SeminarDetailsPage;
